import React, { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  noindex?: boolean;
}

const SEO: React.FC<SEOProps> = ({
  title = 'Nemlingo - Danish Vocabulary for PD2 & PD3 Exams',
  description = 'Vocabulary is the hardest part of learning Danish. Nemlingo\'s decks are built by people who passed PD2 & PD3 — real exam words, learned in context.',
  keywords = 'Danish vocabulary, PD2 exam, PD3 exam, Prøve i Dansk, learn Danish vocabulary, Danish exam preparation, Danish flashcards, language learning, Nemlingo',
  canonicalUrl = 'https://nemlingo.com',
  ogType = 'website',
  ogImage = '/logo512.png',
  noindex = false,
}) => {
  const fullTitle = title.includes('Nemlingo') ? title : `${title} | Nemlingo`;
  
  useEffect(() => {
    // Update document title
    document.title = fullTitle;
    
    // Update meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('robots', noindex ? 'noindex, follow' : 'index, follow');

    // Update Open Graph meta tags
    updateMetaTag('og:title', fullTitle, 'property');
    updateMetaTag('og:description', description, 'property');
    updateMetaTag('og:type', ogType, 'property');
    updateMetaTag('og:url', canonicalUrl, 'property');
    updateMetaTag('og:image', ogImage, 'property');
    
    // Update Twitter Card meta tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', fullTitle);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', ogImage);
    
    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);
    
    // Cleanup function
    return () => {
      // A stale noindex outliving the page that set it would deindex the next
      // page, so drop the tag on unmount and let the default (indexable) stand.
      if (noindex) {
        document.querySelector('meta[name="robots"]')?.remove();
      }
    };
  }, [fullTitle, description, keywords, canonicalUrl, ogType, ogImage, noindex]);
  
  // Helper function to update meta tags
  const updateMetaTag = (name: string, content: string, attributeName: string = 'name') => {
    let metaTag = document.querySelector(`meta[${attributeName}="${name}"]`);
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.setAttribute(attributeName, name);
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', content);
  };
  
  // This component doesn't render anything visible
  return null;
};

export default SEO;
